import type { GroomingMetrics, GroomingRunway } from "@/lib/api/grooming";
import {
  countOf,
  itemsWord,
  monthDay,
  pointsWord,
  sessionsWord,
  sizingLine,
  sprintLine,
  windowDates,
} from "@/lib/grooming/grooming-progress";

/**
 * Copy summary's plain text (epic PI-SPEC-SW-E293, feature PI-SPEC-SW-1505,
 * UX-PM-01, frame E3): the panel's three lines plus the groomed runway, for
 * an e-mail or a Teams chat. Pure and React-free.
 *
 * Counts in words, never a percentage, and never an item list (whoever wants
 * the items opens the panel). Bullets are U+2022, separators U+00B7 and the
 * dates use an en dash.
 */

export interface GroomingSummaryInput {
  /** The ADO project, as the Backlog names it. */
  project: string;
  /** The metrics on screen; `sinceDays` is the window the text names. */
  metrics: GroomingMetrics;
  /** The Backlog read's runway: groomed points and how many sprints of work they are. */
  runway: Pick<GroomingRunway, "groomedPoints" | "sprints">;
  /** The signed-in person's full SpecTree name; left out when unknown. */
  sender: string | null;
  /** Today, for the window's dates and the sign-off date. */
  now?: Date;
}

/** The advisory sentence; sizes stay suggestions. */
export const SUMMARY_ADVISORY = "Claude's sizes are suggestions; the team decides.";

/** Where the text says it came from. */
export const SUMMARY_SOURCE = "From SpecTree Toolbox";

const BULLET = "•";
const DOT = "·";

function sizingSentence(metrics: GroomingMetrics): string {
  const line = sizingLine(metrics.estimateAgreement);
  if (line.kind === "none") return "Sizing: no items sized yet.";
  const times = `${countOf(line.inside, line.count)} times`;
  return line.kind === "early"
    ? `Sizing: the team's size landed inside Claude's range ${times} so far; too few sized items to judge yet.`
    : `Sizing: the team's size landed inside Claude's range ${times}.`;
}

function preparationSentence(metrics: GroomingMetrics): string {
  const coverage = metrics.prepCoverage;
  const sessions = coverage.batches.length;
  if (sessions === 0) return `Preparation: no finished sessions in these ${String(metrics.sinceDays)} days.`;
  return `Preparation: ${countOf(coverage.briefedOrCurrent, coverage.items)} agenda items had a brief before the meeting (${sessionsWord(sessions)}).`;
}

function sprintSentence(metrics: GroomingMetrics): string {
  const line = sprintLine(metrics);
  switch (line.kind) {
    case "entered":
      return `This sprint: ${itemsWord(line.count)} entered ${line.sprint} without being groomed.`;
    case "none-entered":
      return `This sprint: nothing entered ${line.sprint} ungroomed.`;
    case "no-sprint":
      return "This sprint: the team has no current sprint.";
    case "unavailable":
      return "This sprint: could not be read.";
  }
}

/** "1", "2.5": the runway's sprints at one decimal, without a trailing ".0". */
function sprintsNumber(sprints: number): string {
  return String(Math.round(sprints * 10) / 10);
}

function runwaySentence(runway: GroomingSummaryInput["runway"]): string {
  const points = pointsWord(runway.groomedPoints);
  if (runway.sprints === null) return `Groomed and ready: ${points} (no finished sprints yet to compare).`;
  const sprints = sprintsNumber(runway.sprints);
  return `Groomed and ready: ${points}, about ${sprints} ${sprints === "1" ? "sprint" : "sprints"} of work.`;
}

/** The exact text Copy summary puts on the clipboard (frame E3). */
export function buildGroomingSummary({ project, metrics, runway, sender, now = new Date() }: GroomingSummaryInput): string {
  const sinceDays = metrics.sinceDays;
  // A blank name is left out like a missing one (filter drops the empty string).
  const signOff = [SUMMARY_SOURCE, sender?.trim() ?? "", monthDay(now)].filter(Boolean).join(` ${DOT} `);
  return [
    `Grooming for ${project}, last ${String(sinceDays)} days (${windowDates(sinceDays, now)})`,
    "",
    `${BULLET} ${sizingSentence(metrics)}`,
    `${BULLET} ${preparationSentence(metrics)}`,
    `${BULLET} ${sprintSentence(metrics)}`,
    `${BULLET} ${runwaySentence(runway)}`,
    "",
    SUMMARY_ADVISORY,
    signOff,
  ].join("\n");
}
